"use client"

import { useRouter, useSearchParams, type ReadonlyURLSearchParams } from "next/navigation"
import clsx from "clsx"

import type { Nullish, PropsWithClassName, RemoveRecordFromUnion } from "~/@types/generics"
import { Image } from "~/components/ui/Image"
import type { TImage } from "~/components/ui/Image/_data/serializer"
import { Link } from "~/components/ui/Link"
import type { TLink } from "~/components/ui/Link/_data/serializer"
import { CONFORT_PARAM, OPTIONS_PARAM, SIZE_PARAM } from "~/components/ui/Product/ProductHeader/_data/constants"
import { Stack } from "~/components/abstracts/Stack"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import { addMultipleOptionsParamURLToHref } from "~/utils/add-multiple-options-param-url-to-href"
import { addOptionsParamURLToHref } from "~/utils/add-options-param-url-to-href"
import { addPanelSizeAutoOpenParamURLToHref } from "~/utils/add-panel-size-auto-open-param-url-to-href"

import { sprinkles, type Sprinkles } from "~/styles/sprinkles.css"

import * as css from "./styles.css"

export type TColorWithDotImage = {
  value: string
  image: Nullish<TImage>
  link?: Nullish<TLink>
  availableForSale?: boolean
}

export type ColorsSelectorProps = PropsWithClassName<{
  currentColor: Nullish<string>
  sprinklesCss?: Sprinkles
  gap?: RemoveRecordFromUnion<Sprinkles["gap"]>
  showCurrentColor?: boolean
  colorTag?: Nullish<string>
  colorsWithDotImage?: TColorWithDotImage[]
  isBundleSelector?: boolean
  isMobileBundleSizePanelOpen?: boolean
}>

export function ColorsSelector({
  className,
  colorTag,
  colorsWithDotImage,
  currentColor,
  sprinklesCss,
  showCurrentColor = true,
  isBundleSelector = false,
  isMobileBundleSizePanelOpen = false,
}: ColorsSelectorProps) {
  const t = useTranslate()
  const searchParams = useSearchParams()
  const router = useRouter()

  return (
    <div
      className={clsx(sprinkles({ ...(sprinklesCss ?? {}) }), className)}
      data-comp="UI/Product/ProductHeader/ColorsSelector"
    >
      <Stack
        className={css.ProductColorsWrapper}
        gap={15}
        sprinklesCss={{ justifyContent: "center", flexFlow: "row wrap" }}
      >
        {colorsWithDotImage?.map((color, index) => {
          const isActive = color.value === currentColor
          const isAvailableForSale = color.availableForSale ?? true

          const compiledURL = color.link?.href
            ? getLinkHref({
                isBundleSelector,
                isMobileBundleSizePanelOpen,
                href: color.link?.href,
                searchParams,
              })
            : undefined

          return (
            <Link
              key={`selector_color_${index}`}
              aria-label={t("aria_color_switch", { color: color.value })}
              className={clsx(className, css.ProductColorsSelectorItem, {
                [css.buttonVariants.active]: isActive,
                [css.buttonVariants.outOfStock]: !isAvailableForSale,
              })}
              disabled={isActive}
              {...color.link}
              onClick={(e) => {
                if (compiledURL) {
                  e.preventDefault()
                  router.replace(compiledURL, { scroll: false })
                }
              }}
              {...(compiledURL && { href: compiledURL })}
            >
              {color.image && <Image {...color.image} />}
            </Link>
          )
        })}
      </Stack>
      {showCurrentColor && currentColor && (
        <span className={css.colorName}>
          {currentColor}
          {colorTag ? ` - ${colorTag}` : ``}
        </span>
      )}
    </div>
  )
}

function getLinkHref({
  href,
  searchParams,
  isBundleSelector,
  isMobileBundleSizePanelOpen,
}: {
  isBundleSelector: boolean
  isMobileBundleSizePanelOpen: boolean
  href: string
  searchParams: ReadonlyURLSearchParams
}) {
  if (isBundleSelector) {
    const optionsSearchParams = searchParams.get(OPTIONS_PARAM)

    // In all cases we need to add a param to auto open the panel when switching from a color to another
    if (optionsSearchParams) {
      const urlWithParams = addOptionsParamURLToHref(href, optionsSearchParams)

      return isMobileBundleSizePanelOpen ? addPanelSizeAutoOpenParamURLToHref(href, optionsSearchParams) : urlWithParams
    } else {
      return isMobileBundleSizePanelOpen ? addPanelSizeAutoOpenParamURLToHref(href, null) : href
    }
  } else {
    const sizeParams = searchParams.get(SIZE_PARAM)
    const confortParams = searchParams.get(CONFORT_PARAM)

    if (sizeParams) {
      return addMultipleOptionsParamURLToHref(href, sizeParams, confortParams)
    }
  }

  return href
}
